import {TKey} from '@/translations/i18n'
import {columnNameToLabel as patientsColumnNameToLabel, patientEventColumnToLabel} from '@/features/patients/utils'
import {exportColumnNameToLabel as projectsExportColumnNameToLabel} from '@/features/projects/utils'
import {exportColumnNameToLabel as usersExportColumnNameToLabel, userEventColumnToLabel} from '@/features/users/utils'
import {treatmentRequestColumnToLabel} from '@/features/treatment-requests/utils'
import {accessesColumnToLabel} from '@/features/accesses/utils'

/** Minimal translator shape (matches the `t` returned by `useTranslation`). */
type TranslateFn = (key: TKey) => string

/**
 * Per-entity maps from a column key to the same i18n key the data tables use, so the export
 * summary reads with the real application labels (e.g. `birthDate` → "Data di nascita") instead
 * of a humanised guess. Projects/users use their *export* maps (table columns + export-only
 * preset/custom columns) so the export-only fields also resolve instead of falling back to
 * {@link humanizeColumnKey}.
 */
const COLUMN_LABEL_KEY_BY_ENTITY: Partial<Record<string, Record<string, TKey | null>>> = {
    patients: patientsColumnNameToLabel,
    patient_events: patientEventColumnToLabel,
    user_events: userEventColumnToLabel,
    projects: projectsExportColumnNameToLabel,
    users: usersExportColumnNameToLabel,
    treatment_requests: treatmentRequestColumnToLabel,
    // The three accesses export entities share the same column set.
    accesses_raw: accessesColumnToLabel,
    activity_report: accessesColumnToLabel,
    accesses_ria: accessesColumnToLabel
}

/**
 * Humanise an export column key into a readable label, e.g. `birthDate` → "Birth date",
 * `residenceAslDistrictId` → "Residence asl district". Pragmatic fallback used when no
 * entity-specific translation key is available for a column.
 */
export const humanizeColumnKey = (key: string): string => {
    const spaced = key
        .replace(/Id$/, '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase()
    if (!spaced) return key
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Resolve a single column key to its localised label, reusing the data-table translations. */
const resolveColumnLabel = (entity: string, key: string, t: TranslateFn): string => {
    const labelKey = COLUMN_LABEL_KEY_BY_ENTITY[entity]?.[key]
    return labelKey ? t(labelKey) : humanizeColumnKey(key)
}

/**
 * Compact summary of the selected columns; empty when default columns are used.
 * Labels reuse the same translation keys as the entity's data table.
 */
export const summarizeColumns = (
    entity: string,
    appliedColumns: string[] | null | undefined,
    t: TranslateFn
): string => {
    if (!appliedColumns || appliedColumns.length === 0) return ''
    // Defensively de-duplicate: a column is unique by definition, and jobs enqueued before the
    // upstream fix may still carry a repeated key (e.g. patients' duplicated `fullName`).
    return [...new Set(appliedColumns)].map(key => resolveColumnLabel(entity, key, t)).join(', ')
}
